import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Gavel, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AuctionCard } from "@/components/auction-card";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { azError } from "@/lib/az-errors";
import { fetchMyListings } from "@/lib/listings";
import { fetchActiveAuctions, fetchMyAuctions, startAuction } from "@/lib/auctions";

export const Route = createFileRoute("/herrac")({
  head: () => ({
    meta: [
      { title: "Hərrac — 1 AZN-dən başlayan canlı hərraclar | Albazar.az" },
      { name: "description", content: "Albazar.az hərracı: hər hərrac 1 AZN-dən başlayır və 1 saat davam edir. Təklif verin, ən yüksək təklif qalib gəlir." },
      { property: "og:title", content: "Hərrac — Albazar.az" },
      { property: "og:description", content: "1 AZN-dən başlayan 1 saatlıq canlı hərraclar." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://albazar.az/herrac" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://albazar.az/herrac" }],
  }),
  component: AuctionsPage,
});

function AuctionsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: auctions = [], isLoading } = useQuery({
    queryKey: ["auctions", "active"],
    queryFn: () => fetchActiveAuctions(),
    refetchInterval: 15_000,
  });

  // Canlı yeniləmə: yeni təkliflər dərhal görünsün
  useEffect(() => {
    const ch = supabase
      .channel("auctions-live")
      .on("postgres_changes", { event: "*", schema: "public", table: "auctions" }, () =>
        qc.invalidateQueries({ queryKey: ["auctions"] }),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [qc]);

  return (
    <div className="container-x py-6 pb-24">
      <div className="flex items-center gap-3 rounded-3xl bg-gradient-to-r from-primary to-info p-4 text-primary-foreground shadow-elevated">
        <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-background/20"><Gavel className="size-7" /></span>
        <div>
          <h1 className="text-xl font-extrabold">Hərrac</h1>
          <p className="text-xs opacity-90">Hər hərrac 1 AZN-dən başlayır, 1 saat davam edir. Ən yüksək təklif qalib gəlir!</p>
        </div>
      </div>

      <h2 className="mt-6 text-sm font-bold uppercase tracking-wider text-muted-foreground">Aktiv hərraclar</h2>
      {isLoading ? (
        <p className="mt-3 text-sm text-muted-foreground">Yüklənir…</p>
      ) : auctions.length === 0 ? (
        <div className="mt-3 rounded-2xl border border-dashed border-border bg-card/50 p-6 text-center text-sm text-muted-foreground">
          Hazırda aktiv hərrac yoxdur
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {auctions.map((a) => <AuctionCard key={a.id} auction={a} />)}
        </div>
      )}

      {user ? (
        <SellerPanel userId={user.id} />
      ) : (
        <div className="mt-8 rounded-2xl border border-border bg-card p-4 text-center text-sm">
          Malınızı hərraca çıxarmaq üçün <Link to="/giris" className="font-bold text-primary">daxil olun</Link>
        </div>
      )}
    </div>
  );
}

function SellerPanel({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const [busyId, setBusyId] = useState<string | null>(null);
  const { data: listings = [] } = useQuery({ queryKey: ["my-listings-auction", userId], queryFn: () => fetchMyListings(userId) });
  const { data: mine = [] } = useQuery({ queryKey: ["auctions", "mine", userId], queryFn: () => fetchMyAuctions(userId) });

  const activeListingIds = new Set(mine.filter((a) => a.status === "active").map((a) => a.listing_id));
  const eligible = listings.filter((l) => l.status === "active" || l.status === "pending");
  const myPast = mine.filter((a) => a.status !== "active");

  const start = async (id: string) => {
    setBusyId(id);
    try {
      await startAuction(id);
      toast.success("Malınız hərraca çıxarıldı! Hərrac 1 saat davam edəcək.");
      qc.invalidateQueries({ queryKey: ["auctions"] });
    } catch (e) {
      toast.error(azError(e as Error));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="mt-8 space-y-6">
      <div>
        <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Malını hərraca çıxar</h2>
        {eligible.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">
            Hərraca çıxarmaq üçün əvvəlcə <Link to="/yeni-elan" className="font-bold text-primary">elan yerləşdirin</Link>.
          </p>
        ) : (
          <ul className="mt-2 space-y-2">
            {eligible.map((l) => (
              <li key={l.id} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-2">
                <img src={l.images?.[0]} alt="" className="size-12 shrink-0 rounded-xl bg-muted object-cover" />
                <span className="line-clamp-1 flex-1 text-sm font-medium">{l.title}</span>
                {activeListingIds.has(l.id) ? (
                  <span className="text-xs font-semibold text-primary">Hərracdadır</span>
                ) : (
                  <Button size="sm" disabled={busyId === l.id} onClick={() => start(l.id)} className="rounded-xl">
                    <Gavel className="size-4" /> Hərraca çıxar
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {myPast.length > 0 && (
        <div>
          <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Hərrac nəticələrim</h2>
          <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {myPast.map((a) => (
              <div key={a.id} className="space-y-1.5">
                <AuctionCard auction={a} />
                {a.seller_id === userId && a.listing_id && !activeListingIds.has(a.listing_id) && (
                  <Button size="sm" variant="outline" disabled={busyId === a.listing_id} onClick={() => start(a.listing_id!)} className="w-full rounded-xl">
                    <RotateCcw className="size-4" /> Yenidən hərraca çıxar
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
